import React, { useEffect, useState, useCallback } from 'react';
import { StatueData, decodeDna } from 'statue-helpers';
import { Text, Button, useToasts } from '@zeit-ui/react';

import './index.css';
import GalleryItem from '../GalleryItem';
import { getAllOwnerTokenIds, getTokenURI } from '../../utils/contractCalls';
import { useDrizzleHelper } from '../../utils/drizzleHelpers';
import { getMetadata, getPendingRendersDna } from '../../utils/api';
import RollBanner from '../RollBanner';
import renderingInProgress from '../../assets/rendering_in_progress.mp4';
import Loading from '../Loading';

type StatueDataWithDna = StatueData & { dna: string };

// TODO: Add an initialized and refetching states
export default function OwnerCollection() {
  const [, setToast] = useToasts();
  const { drizzle, accounts, isConnected, events } = useDrizzleHelper();
  const [isLoading, setIsLoading] = useState(true);
  const [statueDatas, setStatueDatas] = useState<StatueDataWithDna[]>([]);
  const [statuesToRoll, setStatuesToRoll] = useState<StatueDataWithDna[]>([]);
  const [renderingStatueDatas, setRenderingStatueDatas] = useState<
    StatueDataWithDna[]
  >([]);

  const refetchOwnerTokens = useCallback(() => {
    // setIsLoading(true);
    getAllOwnerTokenIds(drizzle, accounts[0]).then((ids) => {
      Promise.all(ids.map((id) => getTokenURI(drizzle, id)))
        // TODO: Add get multiple metadata to avoid a ton of roundtrips
        .then((uris) => Promise.all(uris.map(getMetadata)))
        .then((allMetadata) => {
          // failry safe using index here since we're the order is
          // always reserved and we have ids as a closure
          setStatueDatas(
            allMetadata.map((metadata, index) => ({
              id: ids[index],
              statue: decodeDna(metadata.name),
              videoUrl: metadata.animation_url,
              dna: metadata.name,
            }))
          );
          setIsLoading(false);
        });
    });
  }, [drizzle, accounts]);

  const refetchPendingRenders = useCallback(() => {
    if (!accounts[0]) {
      return;
    }

    getPendingRendersDna(accounts[0]).then((dnas) => {
      const pendingRenders = statueDatas.length
        ? dnas.map((dna) => ({
            dna,
            id: '',
            videoUrl: renderingInProgress,
            statue: decodeDna(dna),
          }))
        : [];

      setRenderingStatueDatas(pendingRenders);
    });
  }, [accounts, statueDatas]);

  useEffect(() => {
    if (!isConnected || !events.length) {
      setIsLoading(false);
      return;
    }

    const lastEvent = events[events.length - 1];
    console.log(events);
    if (
      lastEvent.returnValues.rollSender.toLowerCase() ===
      accounts[0].toLowerCase()
    ) {
      refetchOwnerTokens();
    }
  }, [events, accounts, isConnected, refetchOwnerTokens]);

  useEffect(() => {
    if (!isConnected) {
      setIsLoading(false);
      return;
    }

    // Quick hack to show loading on init but not on refetch
    setIsLoading(true);
    refetchOwnerTokens();
  }, [isConnected, refetchOwnerTokens]);

  useEffect(() => {
    refetchPendingRenders();
  }, [refetchPendingRenders]);

  const handleToggleClick = (statueData: StatueDataWithDna) => {
    // remove the statue if it exists within statuesToRoll
    if (statuesToRoll.some((s) => s.id === statueData.id)) {
      setStatuesToRoll(statuesToRoll.filter((s) => s.id !== statueData.id));
      return;
    }

    // do nothing if statuesToRoll is "full"
    if (statuesToRoll.length >= 3) {
      return;
    }

    setStatuesToRoll([...statuesToRoll, statueData]);
  };

  console.log('From OwnerCollection', isConnected, accounts);

  return (
    <div className="OwnerCollection">
      {!isConnected ? (
        <Text h2>Please connect to MetaMask!</Text>
      ) : isLoading ? (
        <Loading />
      ) : !statueDatas.length ? (
        <Text h2>You don't have any statues currently. Go buy some!</Text>
      ) : (
        <>
          <RollBanner
            statuesToRoll={statuesToRoll}
            onRollComplete={() => {
              setToast({ text: "We're rendering your new statue now!" });
              setStatuesToRoll([]);
              window.setTimeout(() => {
                // TODO: fix this hack after queue timings are straightened
                // out on the backend
                refetchPendingRenders();
              }, 2000);
            }}
          />
          <div className="list-view">
            {statueDatas.map((statueData) => {
              const { id, statue, videoUrl } = statueData;

              return (
                <GalleryItem
                  key={id}
                  statueId={id}
                  statue={statue}
                  videoUrl={videoUrl}
                  footer={
                    <Button
                      type="abort"
                      style={{ width: '100%' }}
                      onClick={() => handleToggleClick(statueData)}
                    >
                      {statuesToRoll.some((s) => s.id === id)
                        ? 'Remove From Slot'
                        : 'Add To Slot'}
                    </Button>
                  }
                />
              );
            })}

            {renderingStatueDatas.map((renderingStatue, index) => {
              const { id, statue, videoUrl } = renderingStatue;

              return (
                <GalleryItem
                  key={index}
                  statueId={id}
                  statue={statue}
                  videoUrl={videoUrl}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
